/**
 * This module initializes and configures an Algolia search client with InstantSearch.js.
 * It includes custom widgets for a search box and infinite hits, and handles UI updates
 * for empty states and search results.
 */

import {liteClient as algoliasearch} from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {configure} from 'instantsearch.js/es/widgets';
import {connectSearchBox, connectInfiniteHits} from 'instantsearch.js/es/connectors';

/**
 * Initializes the Algolia search functionality.
 */
const algolia = () => {

	// Tracks whether the search result is empty for specific categories
	var isEmptyResult = {
		'product': true,
	};

	// Initializes the Algolia search client with application ID and API key
	const searchClient = algoliasearch(
		window.algoliaAppId,
		window.algoliaSearchKey
	);

	/**
	 * Updates the visibility of the empty state container based on search results.
	 */
	function setEmtpyContainerVisibility() {
		const containerEmpty = document.querySelector('#emptyState');
		containerEmpty.style.display = isEmptyResult['product'] ? '' : 'none';
	}

	// Configures the InstantSearch.js instance
	const search = instantsearch({
		indexName: window.algoliaIndexName || 'products', // Name of the Algolia index
		searchClient,
		searchFunction(helper) {
			const container = document.querySelector('#hitsProduct');
			const modalFooter = document.querySelector('#modal-search-footer');
			container.style.display = helper.state.query.length < 3 ? 'none' : '';

			if (helper.state.query && helper.state.query.length >= 3) {
				const containerInit = document.querySelector('#initialState');
				containerInit.style.display = 'none';

				helper.search();
			} else {
				const containerInit = document.querySelector('#initialState');
				containerInit.style.display = '';
				const containerEmpty = document.querySelector('#emptyState');
				containerEmpty.style.display = 'none';
				modalFooter.classList.add('hidden');

			}
		}
	});

	/**
	 * Custom renderer for the search box widget.
	 * @param {Object} renderOptions - Options provided by the connector.
	 * @param {boolean} isFirstRender - Indicates if this is the first render.
	 */

	let globalIsLastPage = false; // Global variable to track isLastPage

	const renderSearchBox = (renderOptions, isFirstRender) => {
		const {query, refine} = renderOptions;

		const input = document.querySelector('#searchbox');

		if (isFirstRender) {
			input.addEventListener('input', event => {
				refine(event.target.value);
			});
		}

		input.value = query;
	};

	// Connects the custom search box renderer to the InstantSearch.js instance
	const customSearchBox = connectSearchBox(renderSearchBox);

	/**
	 * Custom renderer for the infinite hits widget.
	 * @param {Object} renderOptions - Options provided by the connector.
	 * @param {boolean} isFirstRender - Indicates if this is the first render.
	 */
	const renderInfiniteHits = (renderOptions, isFirstRender) => {
		const {hits, showMore, isLastPage} = renderOptions;
		const container = document.querySelector('#hitsProduct');
		const searchModalBody = document.querySelector('#searchModalBody');

		// Update the global variable
		globalIsLastPage = isLastPage;

		if (isFirstRender) {
			// Create container for hits
			const div = document.createElement('div');
			div.classList.add('mb-2', 'flex', 'flex-col', 'gap-2');
			container.appendChild(div);

			// Create loading indicator
			const loadingIndicator = document.createElement('div');
			loadingIndicator.classList.add('hidden');
			loadingIndicator.id = 'loadingMoreItems';
			loadingIndicator.innerHTML = '<div class="block text-center py-8 px-4 bg-gray-100 rounded-xl w-full">Loading more items...</div>';
			container.appendChild(loadingIndicator);

			// Add scroll event listener to the container
			searchModalBody.addEventListener('scroll', function () {
				const scrollPosition = searchModalBody.scrollTop + searchModalBody.clientHeight;
				const scrollHeight = searchModalBody.scrollHeight;

				// Check if we're near the bottom and not at the last page
				if (scrollHeight - scrollPosition < 200  && !globalIsLastPage) {
					const loadingEl = document.getElementById('loadingMoreItems');

					// Show loading indicator
					if (loadingEl && loadingEl.classList.contains('hidden')) {
						loadingEl.classList.remove('hidden');

						// Load more items after a small delay
						setTimeout(() => {
							search.addWidgets([
								configure({
									hitsPerPage: 5, // Change hitsPerPage for subsequent loads
								}),
							]);
							showMore();
							loadingEl.classList.add('hidden'); // Hide loading indicator after loading
						}, 100);
					}
				}
			});

			return;
		}

		const loadingIndicator = document.getElementById('loadingMoreItems');
		const modalFooter = document.querySelector('#modal-search-footer');
		modalFooter.classList.add('hidden');

		// Update aria-live region for screen readers
		const searchStatus = document.querySelector('#searchStatus');
		if (searchStatus) {
			if (hits.length > 0) {
				searchStatus.textContent = `${hits.length} product${hits.length === 1 ? '' : 's'} found`;
			} else {
				searchStatus.textContent = 'No products found';
			}
		}

		if (hits.length > 0) {
			modalFooter.classList.remove('hidden');
			isEmptyResult['product'] = false;

			// Render the hits
			container.querySelector('div').innerHTML = `
      		${hits.map(item => {

				// Get connections - simple array of strings
				const connections = item.connections || [];
				const mountings = item.mountings || [];
				// Get outputs to show frequency-based connections
				const outputs = item.outputs || [];

				// Build frequency connections from outputs
				const frequencyConnections = outputs.map(output => ({
					frequency: output.frequency || 'Unknown',
					count: 1
				}));

				// Group by frequency and count
				const groupedFrequencies = Object.entries(
					frequencyConnections.reduce((acc, item) => {
						acc[item.frequency] = (acc[item.frequency] || 0) + item.count;
						return acc;
					}, {})
				);
				
				console.log(item.image);

				return `<a href="${item.url}" class="flex max-md:flex-col group">
							<div class="flex items-center
        								border-l border-t overflow-hidden w-full border-gray-300
        								max-md:border-r max-md:rounded-t-2xl
        								md:border-b md:rounded-l-2xl lg:w-24 md:w-20 ${item.is_eol ? 'grayscale opacity-60' : ''}"
        						>
							  ${item.image.length >0 ?	
          					  `<img src="${item.image}" class="aspect-4/3 object-cover mx-auto max-md:h-20"  />`
							  : `<div class="flex items-center justify-center w-full svg:mx-auto svg:size-12 svg:fill-gray-300"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
    <path d="M67.6 44.8L56.3 33.5L33.7 56.1L45 67.4L572.8 595.2L584.1 606.5L606.8 583.9C602.1 579.2 588.8 565.9 566.9 544L576.2 544L576.2 160L448.2 160L416.2 96L224.2 96L192.2 160L182.9 160L67.7 44.8zM212.9 190C215.6 184.6 225.9 163.9 243.9 128L396.3 128C414.1 163.6 424.8 185 428.3 192L544.1 192L544.1 512L534.8 512L421.8 399C428.4 384.7 432.1 368.8 432.1 352C432.1 290.1 382 240 320.1 240C303.3 240 287.4 243.7 273.1 250.3L212.9 190zM397 374.2L298 275.1C305 273.1 312.5 272 320.2 272C364.4 272 400.2 307.8 400.2 352C400.2 359.7 399.1 367.1 397.1 374.2zM64.1 160L64.1 544L453.6 544L421.6 512L96.1 512L96.1 192L101.6 192L69.6 160L64.1 160zM208.1 352C208.1 413.9 258.2 464 320.1 464C335.9 464 350.9 460.7 364.5 454.9L339.3 429.7C333.2 431.2 326.7 432 320.1 432C275.9 432 240.1 396.2 240.1 352C240.1 345.4 240.9 339 242.4 332.8L217.2 307.6C211.3 321.2 208.1 336.2 208.1 352z"/>
</svg> </div>`}
          					</div>
							  <div class="bg-gray-100 p-4 border-gray-300 max-md:rounded-b-2xl md:p-6 md:w-full border md:rounded-r-2xl group-hover:bg-gray-200 transition">
								<div class="md:text-lg">
								  ${item.is_eol ? `<div class="inline-block bg-gray-200 text-gray-600 text-xs font-medium uppercase tracking-wide px-2 py-1 rounded-md mb-3">End of Life</div>` : ''}
								  <strong>${item._highlightResult?.name?.value || item.name}</strong> – ${item._highlightResult?.title_h1?.value || item.title_h1 || ''}
								</div>
								${connections.length > 0 ? `<dl class="flex items-center gap-2 mt-1">
									<dt class="text-gray-500 min-w-32">
										Connections:
								  	</dt>
								  	<dd class="flex flex-wrap items-center gap-2">
										${connections.join(', ')}
							    	</dd>
								</dl>` : ''}
								${groupedFrequencies.length > 0 ? `<dl class="flex items-center gap-2 mt-1">
									<dt class="text-gray-500 min-w-32">
										Frequencies (${groupedFrequencies.length}):
								  	</dt>
								  	<dd class="flex flex-wrap items-center gap-y-2 gap-x-4">
								  	${groupedFrequencies.map(([name, count]) => `
									 	<div class="flex gap-1.5 text-base whitespace-nowrap items-center">
											<div class="bg-black rounded-md p-1 text-white min-w-7 text-center text-xs font-bold">
												${count}×
											</div>
											${name}
										</div>
									`).join('')}
							    	</dd>
								</dl>` : ''}
								${mountings.length > 0 ? `<dl class="flex items-center gap-2 mt-1">
								  <dt class="text-gray-500 min-w-32">
									Mounting:
								  </dt>
								  <dd class="flex flex-wrap items-center gap-2">
									${mountings.join(', ')}
								  </dd>
								</dl>` : ''}
							  </div>
          				</a>
        `;
			}).join('')}`;

			// Hide loading indicator after content loads
			if (loadingIndicator) {
				loadingIndicator.classList.add('hidden');
			}

			setEmtpyContainerVisibility();
		} else {
			isEmptyResult['product'] = true;
			if (loadingIndicator) loadingIndicator.classList.add('hidden');
			container.querySelector('div').innerHTML = ``;
			setEmtpyContainerVisibility();
		}
	};

	// Connects the custom infinite hits renderer to the InstantSearch.js instance
	const customInfiniteHitsProduct = connectInfiniteHits(renderInfiniteHits);

	// Adds widgets to the InstantSearch.js instance
	search.addWidgets([
		configure({
			hitsPerPage: 10, // Number of hits per page
		}),
		customSearchBox(),
		customInfiniteHitsProduct(),
	]);

	// Starts the InstantSearch.js instance
	search.start();
};

export default algolia;
